import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  const [schemas, setSchemas] = useState([]);       // list of schema keys from the API
  const [schemaName, setSchemaName] = useState(''); // current selected schema
  const [taskId, setTaskId] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [autoCheckInterval, setAutoCheckInterval] = useState(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Adjust for your actual domain
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://tg-username-retriever.thisisanurl.xyz';

  // Schema passwords mapping with random 8-character passwords
  const schemaPasswords = {
    "fafa117convbot": "Kj8mP2#x",
    "support_fafa117": "Yw9nL5$q",
    "fafa118convbot": "Hv4cR7@z",
    "support_fafa118": "Bt5mX8#p",
    "fafa123convbot": "Qn6wJ9$k",
    "support_fafa123": "Zd3hF5@m",
    "fafa138convbot": "Mx7vG4#t",
    "support_fafa138": "Wp8cL2$n",
    "fafa178convbot": "Rk5bH9@q",
    "support_fafa178": "Js6nV3#x",
    "fafa191convbot": "Ty4mC7$w",
    "fafa191convbot1": "Fg8pK2@z",
    "support_fafa191": "Xb3hN5#q",
    "support_fafa191_1": "Lm7wR4$t",
    "fafa1x2convbot": "Vk9cB6@n",
    "support_fafa1x2": "Qs2hM8#p",
    "support_fafa212": "Wd5nJ7$x",
    "fafa222convbot": "Hy6bK4@m",
    "support_fafa222": "Pz8vF2#t",
    "fafa288convbot": "Gc3mL5$q",
    "support_fafa288": "Nx7wH9@z",
    "fafa2uconvbot": "Bt4cR6#p",
    "support_fafa2u": "Yk8nV2$m",
    "fafa365convbot": "Mq5hJ7@x",
    "support_fafa365": "Ws3bF9#t",
    "fafa368convbot": "Lz6mK4$n",
    "support_fafa368": "Rx8wC2@q",
    "fafa555convbot": "Dv4hN7#p",
    "support_fafa555": "Jt5bM6$z",
    "fafa666convbot": "Kw9nL3@x",
    "support_fafa666": "Pg2mH8#q",
    "fafa678convbot": "Xc7vF4$t",
    "support_fafa678": "Zs3bK5@n",
    "fafa777convbot": "Qy6wR2#m",
    "support_fafa777": "Hm8nJ9$p",
    "fafa787convbot": "Vt4hC7@x",
    "support_fafa787": "Bk5bL6#q",
    "fafa789convbot": "Nw2mF8$z",
    "support_fafa789": "Gx7vH3@t",
    "fafa800convbot": "Wd9cK4#n",
    "support_fafa800": "Py6bM5$m",
    "fafa855convbot": "Js3nR7@x",
    "fafa855convbot1": "Lq8wC2#p",
    "support_fafa855": "Xt4hF6$z",
    "fafa889convbot": "Mk5bN9@q",
    "support_fafa889": "Hy7vL3#t",
    "fafa88khconvbot": "Wc2mK8$n",
    "support_fafa88kh": "Pz6wJ4@m",
    "fafa988convbot": "Qd9nH5#x",
    "support_fafa988": "Bt3bF7$p",
    "fafa989convbot": "Gs8vR2@q",
    "support_fafa989": "Yk4mC6#z",
    "fafa999convbot": "Nx7wL9$t",
    "support_fafa999": "Wt5hK3@m",
    "k9wincov": "Jq2bM8#n",
    "support_k9kh": "Zc6nF4$x",
    "support_lynsbo": "Py9vH7@p",
    "maha168newbot": "Rd3wK5#q",
    "support_maha168": "Lm8bC2$t",
    "support_sbobetv8": "Xs4nJ6@z",
    "sbobetv8convbot": "Hk7vF9#m"
  };

  // 1) Fetch the schema list as soon as the component mounts
  useEffect(() => {
    async function fetchSchemas() {
      try {
        const response = await fetch(`${API_BASE_URL}/schemas`);
        if (!response.ok) {
          throw new Error('Failed to load schema list');
        }
        const data = await response.json(); // an array of schema names
        setSchemas(data);
        if (data.length > 0) {
          setSchemaName(data[0]); // set default to the first schema
        }
      } catch (error) {
        console.error('Error fetching schemas:', error);
        setSchemas([]); // or handle error
      }
    }
    fetchSchemas();
  }, [API_BASE_URL]);

  // 2) Start Download
  const startDownload = async () => {
    // Validate password
    if (password !== schemaPasswords[schemaName]) {
      setError('Invalid password for this schema');
      return;
    }

    setError(''); // Clear any previous errors
    setIsLoading(true);
    setStatusMessage('Starting download...');
    
    try {
      const url = `${API_BASE_URL}/schemas/${schemaName}/start_download`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });
      
      const data = await response.json();

      if (data.task_id) {
        if (autoCheckInterval) {
          clearInterval(autoCheckInterval);
        }

        setTaskId(data.task_id);
        setStatusMessage(`Task started! Task ID: ${data.task_id}`);
        
        const checkStatusWithTaskId = async () => {
          try {
            const statusUrl = `${API_BASE_URL}/schemas/${schemaName}/status/${data.task_id}`;
            const statusResponse = await fetch(statusUrl);
            const contentType = statusResponse.headers.get('content-type') || '';

            if (statusResponse.ok && contentType.includes('text/csv')) {
              if (autoCheckInterval) {
                clearInterval(autoCheckInterval);
                setAutoCheckInterval(null);
              }
              
              const blob = await statusResponse.blob();
              const downloadUrl = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = downloadUrl;
              link.download = `${schemaName}_users.csv`;
              document.body.appendChild(link);
              link.click();
              link.remove();
              setStatusMessage('CSV downloaded!');
              setTaskId(null);
            } else {
              const statusData = await statusResponse.json();
              if (statusData.status === 'failure') {
                clearInterval(autoCheckInterval);
                setAutoCheckInterval(null);
                setStatusMessage(`Task failed: ${statusData.error || 'Unknown error'}`);
              } else {
                setStatusMessage(`Task status: ${statusData.status}. Checking automatically...`);
              }
            }
          } catch (error) {
            setStatusMessage('Error checking status');
          }
        };
        
        await checkStatusWithTaskId();
        
        const interval = setInterval(checkStatusWithTaskId, 20000); // Check every 20 seconds
        setAutoCheckInterval(interval);
      } else {
        setStatusMessage('Error starting task');
      }
    } catch (error) {
      setStatusMessage('Error sending request');
    } finally {
      setIsLoading(false);
    }
  };

  // Add manual check function
  const checkStatusManually = async () => {
    if (!taskId) return;

    setStatusMessage('Checking status...');
    try {
      const statusUrl = `${API_BASE_URL}/schemas/${schemaName}/status/${taskId}`;
      const statusResponse = await fetch(statusUrl);
      const contentType = statusResponse.headers.get('content-type') || '';

      if (statusResponse.ok && contentType.includes('text/csv')) {
        const blob = await statusResponse.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `${schemaName}_users.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setStatusMessage('CSV downloaded!');
        setTaskId(null);
        if (autoCheckInterval) {
          clearInterval(autoCheckInterval);
          setAutoCheckInterval(null);
        }
      } else {
        const statusData = await statusResponse.json();
        setStatusMessage(`Task status: ${statusData.status}`);
      }
    } catch (error) {
      setStatusMessage('Error checking status');
    }
  };

  // Cleanup interval on unmount or when taskId changes
  useEffect(() => {
    console.log('Cleanup useEffect triggered. Interval:', autoCheckInterval); // Debug log
    return () => {
      if (autoCheckInterval) {
        console.log('Clearing interval:', autoCheckInterval); // Debug log
        clearInterval(autoCheckInterval);
      }
    };
  }, [autoCheckInterval]);

  const handleSchemaSelect = (e) => {
    setSchemaName(e.target.value);
    setPassword('');
    setError('');
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === schemaPasswords[schemaName]) {
      setError('');
      setShowPasswordModal(false);
      startDownload();
    } else {
      setError('Invalid password for this schema');
    }
  };

  const closeModal = () => {
    setShowPasswordModal(false);
    setPassword('');
    setError('');
    setSchemaName('');
  };

  return (
    <div className="admin-container">
      <h1 className="admin-title">Username Portal</h1>

      <div className="schema-selector">
        <label>Select Schema:</label>
        <select
          value={schemaName}
          onChange={handleSchemaSelect}
          disabled={isLoading || taskId}
        >
          <option value="">Select a schema...</option>
          {schemas.map((schema) => (
            <option key={schema} value={schema}>
              {schema}
            </option>
          ))}
        </select>
      </div>

      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Enter Password</h2>
            <form onSubmit={handlePasswordSubmit}>
              <div className="password-input">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                  placeholder="Enter schema password"
                  autoFocus
                  disabled={isLoading}
                />
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <div className="modal-buttons">
                <button 
                  type="submit" 
                  className="button"
                  disabled={isLoading}
                >
                  {isLoading ? 'Processing...' : 'Submit'}
                </button>
                <button 
                  type="button" 
                  className="button secondary" 
                  onClick={closeModal}
                  disabled={isLoading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {taskId && (
        <button 
          className="button check-status"
          onClick={checkStatusManually}
          disabled={isLoading}
        >
          Check Status Manually
        </button>
      )}

      {statusMessage && (
        <div className={`status-message ${taskId ? 'processing' : ''}`}>
          {statusMessage}
        </div>
      )}
    </div>
  );
}

export default App;
